/**
 * Domain resolution — derives { stage, system } from the current hostname, and
 * provides redirect URL building.
 *
 * Target domain structure (stage is the innermost prefix, naming a per-stage
 * zone; system nests under the stage — mirrors the legacy msa hierarchy):
 *   portal  beta.goh.lab126.amazon.dev     / goh.lab126.amazon.dev (prod)
 *   system  msa.beta.goh.lab126.amazon.dev / msa.goh.lab126.amazon.dev (prod)
 *
 * Backward compatibility: the legacy *.msa.lab126.amazon.dev domains (beta /
 * gamma / prod) are recognized as-is and resolved to the matching stage + the
 * default system (MSA), so behavior is unchanged from before this refactor.
 */
import {
  PORTAL,
  DEFAULT_SYSTEM_ID,
  SYSTEMS,
  getSystemBySubdomain,
  type SystemMeta,
} from "./systems";

export type Stage = "beta" | "gamma" | "prod";
export type ResolvedSystem = string | typeof PORTAL; // system key (e.g. 'MSA') or 'PORTAL'

export interface DomainInfo {
  stage: Stage;
  /** Resolved system key (e.g. 'MSA'); 'PORTAL' for the portal */
  system: ResolvedSystem;
  /** Whether this is the portal page */
  isPortal: boolean;
}

/** Portal root domain (without stage prefix); all stages' portal/system domains hang off it */
export const PORTAL_ROOT = "goh.lab126.amazon.dev";
/** Portal page path: the root path on the portal host redirects here */
export const PORTAL_PATH = "/goh";
/** Legacy domain root (transition-period compatibility) */
const LEGACY_ROOT = "msa.lab126.amazon.dev";

const STAGE_PREFIXES: Stage[] = ["beta", "gamma"]; // prod has no prefix

export function isLocalHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0"
  );
}

/**
 * Whether the current environment is local development (localhost, etc.).
 * Used only for dev conveniences like local login-free preview; always returns
 * false in production.
 */
export function isLocalDev(): boolean {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return isLocalHost(host);
}

/**
 * Persistence key for the locally selected system (dev only).
 *
 * In production the system comes from the hostname subdomain (recorded in the
 * domain itself, never lost). Local dev can only simulate it via the `?system=`
 * query, and client-side navigation or manual URL edits can drop that query,
 * silently reverting the system to its default. To prevent that, the locally
 * resolved system is stored in sessionStorage so it can be read back when the
 * query is missing — keeping local preview stable so the system never
 * "disappears". sessionStorage (not localStorage) is used so it clears when the
 * tab closes and does not leak into the next session.
 */
const LOCAL_SYSTEM_STORAGE_KEY = "msa.dev.system";

function readStoredSystem(): SystemMeta | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const sub = window.sessionStorage.getItem(LOCAL_SYSTEM_STORAGE_KEY);
    return sub ? getSystemBySubdomain(sub) : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredSystem(subdomain: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (subdomain) {
      window.sessionStorage.setItem(LOCAL_SYSTEM_STORAGE_KEY, subdomain);
    } else {
      window.sessionStorage.removeItem(LOCAL_SYSTEM_STORAGE_KEY);
    }
  } catch {
    // Degrade silently when sessionStorage is unavailable (private mode, etc.);
    // only the current load's query takes effect in that case.
  }
}

/**
 * Read the ?system= override from the URL query (dev only) and persist it:
 *  - `?system=<known>` → remember that system (readable later even if the query is dropped)
 *  - `?system=portal`  → explicitly clear it and switch back to the portal
 *  - no query          → read back the last persisted choice (fallback for a dropped query)
 */
function getSystemOverrideFromQuery(): SystemMeta | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = new URLSearchParams(window.location.search).get("system");
    if (!raw) {
      // Query missing: fall back to the persisted choice so the system isn't
      // accidentally lost and reverted to the default.
      return readStoredSystem();
    }
    if (raw.toLowerCase() === "portal") {
      writeStoredSystem(null); // Explicit portal: clear persistence, let the caller treat as empty
      return undefined;
    }
    const sys = getSystemBySubdomain(raw);
    if (sys) {
      writeStoredSystem(sys.subdomain); // Remember this choice
      return sys;
    }
    // Unrecognized system value: ignore the query, fall back to the persisted choice
    return readStoredSystem();
  } catch {
    return undefined;
  }
}

/**
 * Parse the new domain structure *.goh.lab126.amazon.dev.
 * Returns null when it isn't the new structure (deferred to the legacy /
 * local fallback handling).
 */
function parseGohHost(hostname: string): DomainInfo | null {
  if (hostname !== PORTAL_ROOT && !hostname.endsWith("." + PORTAL_ROOT)) {
    return null;
  }
  // Take the prefix segments left after removing PORTAL_ROOT. The stage is the
  // INNERMOST prefix (last segment, closest to the apex); the system nests in
  // front of it, e.g.
  //   'msa.beta.goh...' → ['msa','beta']  → stage beta, system msa
  //   'beta.goh...'     → ['beta']        → stage beta, portal
  //   'msa.goh...'      → ['msa']         → stage prod, system msa
  //   'goh...'          → []              → stage prod, portal
  const prefix = hostname === PORTAL_ROOT
    ? ""
    : hostname.slice(0, hostname.length - PORTAL_ROOT.length - 1);
  const segments = prefix ? prefix.split(".") : [];

  let stage: Stage = "prod";
  if (segments.length && STAGE_PREFIXES.includes(segments[segments.length - 1] as Stage)) {
    stage = segments.pop() as Stage;
  }

  if (segments.length === 0) {
    return { stage, system: PORTAL, isPortal: true };
  }
  const sys = getSystemBySubdomain(segments[0]);
  if (sys) {
    return { stage, system: sys.key, isPortal: false };
  }
  // Has a prefix but it's unrecognized → treat as portal to avoid landing on the wrong system
  return { stage, system: PORTAL, isPortal: true };
}

/**
 * Parse the legacy domain structure *.msa.lab126.amazon.dev (transition-period compatibility).
 * Returns null when it isn't the legacy structure.
 */
function parseLegacyHost(hostname: string): DomainInfo | null {
  const defaultKey = SYSTEMS[DEFAULT_SYSTEM_ID].key;
  if (hostname === LEGACY_ROOT) {
    return { stage: "prod", system: defaultKey, isPortal: false };
  }
  if (hostname === "beta." + LEGACY_ROOT) {
    return { stage: "beta", system: defaultKey, isPortal: false };
  }
  if (hostname === "gamma." + LEGACY_ROOT) {
    return { stage: "gamma", system: defaultKey, isPortal: false };
  }
  // Handle alpha.msa.* → beta (aligned with the in-package config; alpha still uses the beta config)
  if (hostname === "alpha." + LEGACY_ROOT) {
    return { stage: "beta", system: defaultKey, isPortal: false };
  }
  return null;
}

/**
 * Resolve the current (or given) hostname → DomainInfo.
 * @param localDevStage default stage for local development (defaults to beta)
 */
export function resolveDomain(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : "",
  localDevStage: Stage = "beta",
): DomainInfo {
  // 1) New structure takes priority
  const goh = parseGohHost(hostname);
  if (goh) return goh;

  // 2) Legacy structure compatibility
  const legacy = parseLegacyHost(hostname);
  if (legacy) return legacy;

  // 3) Local development: support the ?system= override, otherwise default to the portal
  if (isLocalHost(hostname)) {
    const override = getSystemOverrideFromQuery();
    if (override) {
      return { stage: localDevStage, system: override.key, isPortal: false };
    }
    return { stage: localDevStage, system: PORTAL, isPortal: true };
  }

  // 4) Unknown domain fallback: portal
  return { stage: localDevStage, system: PORTAL, isPortal: true };
}

/** Convenience: the current stage */
export function getCurrentStage(localDevStage: Stage = "beta"): Stage {
  return resolveDomain(undefined, localDevStage).stage;
}

/** Convenience: the current system key; the portal returns the default system key (a permission request needs a concrete system) */
export function getCurrentSystemKey(): string {
  const info = resolveDomain();
  return info.isPortal ? SYSTEMS[DEFAULT_SYSTEM_ID].key : (info.system as string);
}

/** Convenience: whether this is the portal host */
export function isPortalHost(): boolean {
  return resolveDomain().isPortal;
}

/** Convenience: whether the current host is the Atlas system */
export function isAtlasHost(): boolean {
  const info = resolveDomain();
  if (info.isPortal) return false;
  const sys = getSystemBySubdomain("atlas");
  return sys !== undefined && info.system === sys.key;
}

/**
 * Build the full URL for a system at a given stage (for full-page redirects).
 * prod has no stage prefix; beta/gamma include one.
 * In local development (localhost) the current origin is kept and only the
 * ?system= query is switched, to make the redirect logic easy to verify.
 */
export function buildSystemUrl(subdomain: string, stage?: Stage): string {
  const currentHost =
    typeof window !== "undefined" ? window.location.hostname : "";
  const currentProtocol =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  const s = stage ?? getCurrentStage();

  // Local development: don't switch to real domains; simulate the system switch with a query param
  if (isLocalHost(currentHost)) {
    const port =
      typeof window !== "undefined" && window.location.port
        ? ":" + window.location.port
        : "";
    return `${currentProtocol}//${currentHost}${port}/?system=${subdomain}`;
  }

  // Stage is the innermost prefix (names the per-stage zone); the system nests
  // in front of it: prod → 'msa.goh...'; beta → 'msa.beta.goh...'.
  const stageZone = s === "prod" ? PORTAL_ROOT : `${s}.${PORTAL_ROOT}`;
  return `https://${subdomain}.${stageZone}`;
}

/** Build the portal URL (for a full-page redirect back to the portal) */
export function buildPortalUrl(stage?: Stage): string {
  const currentHost =
    typeof window !== "undefined" ? window.location.hostname : "";
  const currentProtocol =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  const s = stage ?? getCurrentStage();

  if (isLocalHost(currentHost)) {
    const port =
      typeof window !== "undefined" && window.location.port
        ? ":" + window.location.port
        : "";
    return `${currentProtocol}//${currentHost}${port}/?system=portal`;
  }

  const stagePrefix = s === "prod" ? "" : `${s}.`;
  return `https://${stagePrefix}${PORTAL_ROOT}${PORTAL_PATH}`;
}
